/**
 * Mirrors `backend/src/issues/issues.types.ts`. Keep the two in step: the
 * backend is the source of truth for these shapes.
 */

export type IssueStatus = 'exploring' | 'active' | 'decided' | 'dormant';
export type StanceSource = 'stated' | 'observed' | 'inferred' | 'ai';
export type StanceConfidence = 'low' | 'medium' | 'high';

/** One thing we could do, and who it is expected to move */
export interface ScenarioLever {
  id: string;
  label: string;
  description?: string | null;
  cost?: string | null;
  enabled: boolean;
  effects: {
    subjectType: 'stakeholder' | 'organization';
    subjectId: string;
    subjectLabel: string;
    delta: number;
    note?: string | null;
  }[];
  segmentEffects?: { category: string; delta: number; note?: string | null }[];
}

export type SubjectType = 'stakeholder' | 'organization';

export interface SubjectRef {
  type: SubjectType;
  id: string;
  label: string;
}

/** The −2…+2 scale, named once so every surface says the same words */
export const POSITION_LABELS: Record<number, string> = {
  [-2]: 'Strongly opposed',
  [-1]: 'Opposed',
  0: 'Neutral',
  1: 'Supportive',
  2: 'Champion',
};

export const POSITION_MIN = -2;
export const POSITION_MAX = 2;

/* ------------------------------------------------------------------ */
/* Issues                                                              */
/* ------------------------------------------------------------------ */

export interface IssueView {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  category: string | null;
  categoryLabel: string | null;
  status: IssueStatus;
  priority: number;
  ourPosition: number;
  ourPositionNote: string | null;
  decisionDate: string | null;
  owner: { id: string; name: string } | null;
  tags: string[];
  /** Where the issue stands right now, from current stances */
  balance: BalanceOfForces;
  stanceCount: number;
  /** Stance changes in the last 14 days */
  recentMovements: number;
  createdAt: string;
  updatedAt: string;

  /**
   * How closely this record is held — see `features/confidentiality`. The API
   * only ever returns records the caller may see, so this is for display
   * (the lock marker) rather than for filtering.
   */
  confidentiality?: 'normal' | 'restricted' | 'secret';
  confidentialityNote?: string | null;
}

export interface CreateIssueInput {
  title: string;
  summary?: string | null;
  category?: string | null;
  status?: IssueStatus;
  priority?: number;
  ourPosition?: number;
  ourPositionNote?: string | null;
  decisionDate?: string | null;
  ownerId?: string | null;
  tags?: string[];
}

export type UpdateIssueInput = Partial<CreateIssueInput> & {
  archived?: boolean;
};

export interface IssueQuery {
  status?: IssueStatus;
  category?: string;
  ownerId?: string;
  search?: string;
  includeArchived?: boolean;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* Stances                                                             */
/* ------------------------------------------------------------------ */

export interface StanceView {
  id: string;
  issueId: string;
  subject: SubjectRef;
  position: number;
  positionLabel: string;
  issueInfluence: number;
  confidence: StanceConfidence;
  source: StanceSource;
  rationale: string | null;
  evidenceUrl: string | null;
  signalId: string | null;
  asOf: string;
  recordedBy: { id: string; name: string } | null;
  createdAt: string;
  /** Movement since the previous reading of the same subject */
  previousPosition?: number | null;

  /**
   * How closely this record is held — see `features/confidentiality`. The API
   * only ever returns records the caller may see, so this is for display
   * (the lock marker) rather than for filtering.
   */
  confidentiality?: 'normal' | 'restricted' | 'secret';
  confidentialityNote?: string | null;
}

export interface RecordStanceInput {
  subjectType: SubjectType;
  subjectId: string;
  position: number;
  issueInfluence?: number;
  confidence?: StanceConfidence;
  source?: StanceSource;
  rationale?: string | null;
  evidenceUrl?: string | null;
  signalId?: string | null;
  /** Defaults to now; set it when recording something that was true earlier */
  asOf?: string;
}

/** One subject's arc on one issue */
export interface StanceHistory {
  subject: SubjectRef;
  entries: StanceView[];
}

export interface MovementEntry {
  stance: StanceView;
  from: number | null;
  to: number;
  /** Positive when the move is towards our own position */
  towardsUs: boolean | null;
}

/* ------------------------------------------------------------------ */
/* The map                                                             */
/* ------------------------------------------------------------------ */

/**
 * Support and opposition weighted by how much each actor can affect the
 * outcome — a headcount is misleading when one regulator outweighs twenty
 * supporters.
 */
export interface BalanceOfForces {
  support: number;
  oppose: number;
  neutral: number;
  /** −100 (all against) … +100 (all behind us), influence-weighted */
  index: number;
  /** How lopsided the influence is, whoever holds it: 0–1 */
  concentration: number;
  counts: { champion: number; supportive: number; neutral: number; opposed: number; stronglyOpposed: number };
  /** Watched actors with influence but no recorded position */
  unknownInfluence: number;
}

export interface MapNode {
  subject: SubjectRef;
  position: number | null;
  issueInfluence: number;
  confidence: StanceConfidence | null;
  /** Directory influence, when no issue-specific figure was given */
  directoryInfluence: number | null;
  category: string | null;
  lastMoved: string | null;
  /** Community id from the relationship network, for bloc colouring */
  bloc: number | null;
  /** Connects the two camps — the person a message can travel through */
  isBridge: boolean;
  /** Undecided or lightly held, with enough influence to matter */
  isSwing: boolean;
}

export interface MapEdge {
  source: string;
  target: string;
  kind: string;
  label: string | null;
  weight: number;
  /** True when the two ends sit on opposite sides of the issue */
  crossesCamps: boolean;
}

export interface IssueMap {
  issueId: string;
  balance: BalanceOfForces;
  nodes: MapNode[];
  edges: MapEdge[];
  /** Blocs found in the relationship network, described by their stance */
  blocs: {
    id: number;
    label: string;
    size: number;
    meanPosition: number;
    influence: number;
    members: SubjectRef[];
  }[];
  /** Who to talk to first, and why — ranked, with the reasoning shown */
  priorities: {
    subject: SubjectRef;
    reason: string;
    score: number;
    kind: 'swing' | 'bridge' | 'blocker' | 'amplifier' | 'unknown';
  }[];
  generatedAt: string;
}

/* ------------------------------------------------------------------ */
/* Scenarios                                                           */
/* ------------------------------------------------------------------ */

export interface ScenarioView {
  id: string;
  issueId: string;
  name: string;
  description: string | null;
  levers: ScenarioLever[];
  assumptions: string | null;
  propagation: number;
  result: ScenarioProjection | null;
  resultAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SaveScenarioInput {
  name: string;
  description?: string | null;
  levers?: ScenarioLever[];
  assumptions?: string | null;
  propagation?: number;
}

/** What one actor is projected to do, and why */
export interface ProjectedMove {
  subject: SubjectRef;
  from: number;
  to: number;
  delta: number;
  /** 'Direct effect of X' or 'follows Ministry of Mines (ally, 2 steps)' */
  because: string;
  direct: boolean;
  /** Crossed from opposing to supporting, or the reverse */
  flipped: boolean;
  issueInfluence: number;
}

export interface ScenarioProjection {
  scenarioId: string | null;
  before: BalanceOfForces;
  after: BalanceOfForces;
  moves: ProjectedMove[];
  flips: { gained: SubjectRef[]; lost: SubjectRef[] };
  /**
   * Who to secure first: the actors whose own movement drags the most weight
   * behind them, so the sequence is not just 'the most important person'.
   */
  sequence: {
    subject: SubjectRef;
    reason: string;
    leverage: number;
  }[];
  /** Named levers that changed nothing — worth knowing before conceding them */
  ineffective: string[];
  computedAt: string;
}

/* ------------------------------------------------------------------ */
/* Overview                                                            */
/* ------------------------------------------------------------------ */

export interface IssuesOverview {
  total: number;
  byStatus: Record<IssueStatus, number>;
  /** Issues whose balance worsened in the last 14 days */
  deteriorating: { issue: { id: string; title: string; slug: string }; change: number }[];
  decisionsSoon: { id: string; title: string; slug: string; decisionDate: string }[];
  movements7Days: number;
  unassessed: number;
}
